import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Bug,
  HelpCircle,
  Lightbulb,
  Mail,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { formatDateTime } from '@/lib/locale'
import { useMyContactQueries } from '@/features/contactSales/queries'
import { describeStatus } from '@/features/contactSales/status'
import type { ContactCategory, ContactQueryRow } from '@/features/contactSales/types'

const CATEGORY_META: Record<ContactCategory, { label: string; icon: LucideIcon }> = {
  sales:   { label: 'Talk to Sales',     icon: Mail },
  bug:     { label: 'Report an Issue',   icon: Bug },
  feature: { label: 'Request a Feature', icon: Lightbulb },
  support: { label: 'Get Help',          icon: HelpCircle },
}

const fmt = (v: string | null) => (v ? formatDateTime(v) : '-')

export function MyQueriesPage() {
  const navigate = useNavigate()
  const listQ = useMyContactQueries()

  const rows = listQ.data?.messages ?? []

  const columns: ColumnDef<ContactQueryRow>[] = [
    {
      accessorKey: 'created_at',
      header: 'Submitted',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmt(row.original.created_at)}</span>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Type',
      cell: ({ row }) => {
        const meta = CATEGORY_META[row.original.category]
        const Icon = meta.icon
        return (
          <span className="inline-flex items-center gap-1.5 text-xs">
            <Icon className="size-3.5 text-muted-foreground" />
            {meta.label}
          </span>
        )
      },
    },
    {
      accessorKey: 'subject',
      header: 'Subject',
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[360px] text-xs">
          {row.original.subject || row.original.description.slice(0, 80)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const desc = describeStatus(row.original.status, row.original.category)
        const Icon = desc.icon
        return (
          <Badge variant={desc.badgeVariant} className="gap-1">
            <Icon className="size-3" />
            {desc.label}
          </Badge>
        )
      },
    },
    {
      id: 'replies',
      header: 'Replies',
      cell: ({ row }) =>
        row.original.reply_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs">
            <MessageSquare className="size-3.5 text-muted-foreground" />
            {row.original.reply_count}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate(`/contact-sales/queries/${row.original.id}`)}
        >
          Open
        </Button>
      ),
    },
  ]

  return (
    <Page
      title="Your queries"
      description="Sales inquiries, bug reports, feature requests, and support questions submitted from your workspace."
      width="wide"
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/contact-sales')}>
            <ArrowLeft className="size-4" />
            Back to Contact us
          </Button>
          <Button onClick={() => navigate('/contact-sales')}>New query</Button>
        </>
      }
    >
      {listQ.isLoading ? (
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : listQ.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load your queries.</AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No queries yet"
          description="When you contact us, your messages and Bokapsys' replies show up here."
          action={
            <Button onClick={() => navigate('/contact-sales')}>Send a message</Button>
          }
        />
      ) : (
        <Card>
          <CardContent className="p-3">
            <DataTable columns={columns} rows={rows} />
          </CardContent>
        </Card>
      )}
    </Page>
  )
}
